import React, { useState, useEffect } from 'react'

interface DecimalInputProps {
  value: number | string | null | undefined
  name: string
  id?: string
  onChange: (name: string, value: number | null) => void
  className?: string
  readOnly?: boolean
  disabled?: boolean
  placeholder?: string
  // Fractional digits accepted. Omitted means unlimited.
  decimals?: number
  // What an emptied field commits. null clears a nullable column, where 0 would
  // instead store a meaningful zero.
  emptyValue?: number | null
  ariaLabel?: string
  ariaInvalid?: boolean
  ariaDescribedBy?: string
}

const asString = (value: number | string | null | undefined) => String(value ?? '')

// A number input sanitises anything that is not a valid floating-point number to
// '', and "1." is not one -- so the decimal point never survives a keystroke.
// Hence a text input holding the half-typed string locally and committing a
// parsed number on blur.
const DecimalInput = ({
  value,
  name,
  id,
  onChange,
  className = '',
  readOnly = false,
  disabled = false,
  placeholder,
  decimals,
  emptyValue = 0,
  ariaLabel,
  ariaInvalid,
  ariaDescribedBy
}: DecimalInputProps) => {
  const [localValue, setLocalValue] = useState(asString(value))

  useEffect(() => {
    setLocalValue(asString(value))
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value.replace(',', '.')
    const pattern = decimals === undefined
      ? /^\d*\.?\d*$/
      : new RegExp(`^\\d*${decimals > 0 ? `\\.?\\d{0,${decimals}}` : ''}$`)

    if (nextValue === '' || pattern.test(nextValue)) {
      setLocalValue(nextValue)
    }
  }

  const handleBlur = () => {
    const parsed = parseFloat(localValue)
    const rounded = decimals === undefined ? parsed : parseFloat(parsed.toFixed(decimals))
    const committed = isNaN(parsed) ? emptyValue : rounded

    onChange(name, committed)
    setLocalValue(asString(committed))
  }

  return (
    <input
      className={className}
      type='text'
      inputMode='decimal'
      id={id}
      name={name}
      value={localValue}
      readOnly={readOnly}
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}

export default DecimalInput
